import type { ComponentType } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import type { AuthConfig } from '../types/app-config';
import type { ShadcnPageConfig } from './schema';

export interface LayoutRendererProps {
  page: ShadcnPageConfig;
  apiBaseUrl?: string;
  auth?: AuthConfig;
  queryClient: QueryClient;
}

export interface LayoutRenderer {
  type: string;
  schema: z.ZodType;
  component: ComponentType<LayoutRendererProps>;
}

export interface WidgetRenderer {
  type: string;
  schema: z.ZodType;
  component: ComponentType<Record<string, unknown>>;
}

class RuntimePluginRegistry {
  private readonly layoutRenderers = new Map<string, LayoutRenderer>();

  private readonly widgetRenderers = new Map<string, WidgetRenderer>();

  register(layoutType: string, renderer: LayoutRenderer): void {
    const normalizedType = layoutType.trim();
    if (!normalizedType) {
      throw new Error('layoutType is required when registering a layout renderer.');
    }

    this.layoutRenderers.set(normalizedType, renderer);
  }

  resolve(layoutType: string): LayoutRenderer | null {
    const normalizedType = layoutType.trim();
    if (!normalizedType) {
      return null;
    }

    return this.layoutRenderers.get(normalizedType) ?? null;
  }

  list(): string[] {
    return Array.from(this.layoutRenderers.keys());
  }

  registerWidget(widgetType: string, renderer: WidgetRenderer): void {
    const normalizedType = widgetType.trim();
    if (!normalizedType) {
      throw new Error('widgetType is required when registering a widget renderer.');
    }

    this.widgetRenderers.set(normalizedType, renderer);
  }

  resolveWidget(widgetType: string): WidgetRenderer | null {
    const normalizedType = widgetType.trim();
    if (!normalizedType) {
      return null;
    }

    return this.widgetRenderers.get(normalizedType) ?? null;
  }

  listWidgets(): string[] {
    return Array.from(this.widgetRenderers.keys());
  }
}

export const PluginRegistry = new RuntimePluginRegistry();

export function registerLayout(layoutType: string, renderer: LayoutRenderer): void {
  PluginRegistry.register(layoutType, renderer);
}

export function registerWidget(widgetType: string, renderer: WidgetRenderer): void {
  PluginRegistry.registerWidget(widgetType, renderer);
}
